import React from "react";
import { Box, MenuItem, SxProps, Tooltip, useMediaQuery } from "@mui/material";
import PermissionGuard from "../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../core/models/PermissionModels";
/* WhatsApp sunset 2026-03-14 — IconButton was only used for WhatsApp button */
import { useNavigate } from "react-router-dom";
import { BuildOutlined, ContentCopy, TuneOutlined, RestoreOutlined, ArchiveOutlined, GroupWork as GroupWorkIcon, PlaceOutlined } from "@mui/icons-material";
/* WhatsApp sunset 2026-03-14 — channel deactivated, web chat is sole channel */
// import whatsappLogo from "../../../shared/assets/images/whatsapp-logo.png";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import QrCodeIcon from "@mui/icons-material/QrCode";
import OverflowTooltip from "../../../shared/components/OverflowTooltip";
import TableActionButton from "../../../shared/components/tables/TableActionButton";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import { Machine } from "../models/models";
import useTranslation from "../../../shared/hooks/useTranslation";
import {
  getMachineDisplayName,
  getProductionYearDisplay,
  getBrandDisplayName,
  getMachineName,
  formatManufacturerWithHierarchy,
} from "../../../shared/utils/machineUtils";
import { dt } from "../utils/dt";
import { ImageGallery, useGalleryImages } from "../../../shared/components/gallery";
import MidasCardRow from "./readiness/MidasCardRow";

// =====================================================================
//  HEALTH INDICATOR
// =====================================================================

const getHealthColor = (openServiceReportsCount: number): string => {
  if (openServiceReportsCount === 0) return dt.machinery.health.green;
  if (openServiceReportsCount <= 2) return dt.machinery.health.yellow;
  return dt.machinery.health.red;
};

// =====================================================================
//  GROUP PILL
// =====================================================================

const GROUP_COLORS = [
  "#3498DB", "#8E44AD", "#27AE60", "#E67E22", "#E74C3C",
  "#1ABC9C", "#2980B9", "#9B59B6", "#F39C12", "#16A085",
];

const getGroupColor = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return GROUP_COLORS[Math.abs(hash) % GROUP_COLORS.length];
};

// =====================================================================
//  COMPONENT
// =====================================================================

interface RedesignedMachineCardProps {
  machine: Machine;
  showActions?: boolean;
  isNew?: boolean;
  groupNames?: string[];
  /**
   * DR-4 (item 8): opt-in for the MIDAS readiness row. Default FALSE — the row
   * only renders where the host explicitly asks (the Machinery overview).
   */
  showMidasRow?: boolean;
  /** DR-4: live-connected — sourced once per page from the batch status fetch. */
  connected?: boolean;
  onQRCode?: (machine: Machine) => void;
  /* WhatsApp sunset 2026-03-14 — channel deactivated, web chat is sole channel */
  // onWhatsAppBot?: (machine: Machine) => void;
  onDecommission?: (machine: Machine) => void;
  onRestore?: (machine: Machine) => void;
  onEdit?: (machine: Machine) => void;
  onEditDetails?: (machine: Machine) => void;
  onCreateServiceRequest?: (machine: Machine) => void;
  onCreateServiceReport?: (machine: Machine) => void;
  onDuplicate?: (machine: Machine) => void;
  onAssignGroup?: (machine: Machine) => void;
  sx?: SxProps;
}

const RedesignedMachineCard = ({
  machine,
  showActions,
  isNew,
  groupNames,
  showMidasRow,
  connected,
  onQRCode,
  /* WhatsApp sunset 2026-03-14 — channel deactivated, web chat is sole channel */
  // onWhatsAppBot,
  onEdit,
  onEditDetails,
  onCreateServiceRequest,
  onCreateServiceReport,
  onDuplicate,
  onDecommission,
  onRestore,
  onAssignGroup,
  sx,
}: RedesignedMachineCardProps) => {
  const confirm = useConfirmation();
  const navigate = useNavigate();
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");
  const isPhone = useMediaQuery("(max-width:768px)");
  const isTablet = useMediaQuery("(max-width:1024px)");

  const isClickable = showActions || false;
  const isDecommissioned = machine.isDecommissioned === true;

  const handleCardClick = () => {
    if (!isClickable) return;
    navigate(`/machinery/${machine.id}`);
  };

  const stopProp = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const healthColor = getHealthColor(machine.openServiceReportsCount);
  const healthTooltip =
    machine.openServiceReportsCount === 0
      ? translate("healthNoIssues")
      : translate("healthOpenReports", { count: machine.openServiceReportsCount });

  const displayName = getMachineDisplayName(machine);
  const subtitle = getMachineName(machine);
  const brandName = getBrandDisplayName(machine);
  const yearDisplay = getProductionYearDisplay(machine);

  const galleryImages = useGalleryImages({
    type: "machine",
    image: machine?.image?.content || null,
    catalogImageUrl: machine?.catalogModel?.imageStandardUrl || machine?.catalogModel?.imageUrl || null,
    catalogImages: machine?.catalogModel?.images || [],
    files: machine?.files?.map(f => ({ name: f.name, content: f.content })) || [],
  });

  const effectiveGroupNames = groupNames ?? (machine as any).groupNames ?? [];

  // Location display: "Parent > Child" or just "Child"
  const locationDisplay = machine.locationName
    ? machine.parentLocationName
      ? `${machine.parentLocationName} > ${machine.locationName}`
      : machine.locationName
    : null;

  // Spec row helper (display-only)
  const SpecRow = ({ label, value }: { label: string; value: string | undefined }) => (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px", minWidth: 0 }}>
      <Box sx={{ fontSize: "12px", fontFamily: dt.font, fontWeight: 400, color: dt.text.tertiary, whiteSpace: "nowrap", flexShrink: 0 }}>
        {label}
      </Box>
      <Box sx={{ fontSize: "13px", fontFamily: dt.fontMono, fontWeight: 500, color: dt.text.primary, letterSpacing: "0.3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
        {value || "\u2014"}
      </Box>
    </Box>
  );

  // =====================================================================
  //  PHONE LAYOUT
  // =====================================================================

  if (isPhone) {
    return (
      <Box
        onClick={handleCardClick}
        sx={{
          background: dt.machinery.card.bg,
          border: isDecommissioned ? dt.decommissioned.card.borderStyle : dt.machinery.card.border,
          borderRadius: dt.radiusSm, padding: "14px", boxShadow: dt.machinery.card.shadow,
          position: "relative", width: "100%", boxSizing: "border-box", overflow: "hidden", fontFamily: dt.font,
          opacity: isDecommissioned ? dt.decommissioned.card.opacity : 1,
          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          ...(isClickable ? { cursor: "pointer", "&:hover": { transform: "translateY(-2px)", boxShadow: dt.machinery.card.shadowHover, opacity: isDecommissioned ? dt.decommissioned.card.opacityHover : 1 } } : {}),
          ...(sx as any),
        }}
      >
        {/* NEW badge - phone layout */}
        {isNew && (
          <Box
            sx={{
              position: "absolute",
              top: "10px",
              right: "10px",
              zIndex: 2,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: dt.machinery.newBadge.bg,
              color: dt.machinery.newBadge.text,
              border: `1px solid ${dt.machinery.newBadge.border}`,
              borderRadius: "10px",
              padding: "2px 10px",
              fontSize: "10px",
              fontWeight: 700,
              fontFamily: dt.font,
              textTransform: "uppercase",
              letterSpacing: "1.2px",
              lineHeight: "16px",
              whiteSpace: "nowrap",
              pointerEvents: "none",
              animation: "newMachinePulse 2s ease-in-out 3",
            }}
          >
            {translate("new")}
          </Box>
        )}
        {/* Health indicator dot -- absolute top-right, matching Card view */}
        {showActions && !isDecommissioned && (
          <Tooltip title={healthTooltip} arrow placement="top">
            <Box
              sx={{
                position: "absolute",
                top: "10px",
                right: "10px",
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                backgroundColor: healthColor,
                boxShadow: `0 0 6px ${healthColor}80`,
                zIndex: 1,
                transition: "transform 0.2s ease",
                "&:hover": { transform: "scale(1.3)" },
              }}
            />
          </Tooltip>
        )}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "10px" }}>
          {isDecommissioned && (
            <Box sx={{ fontSize: "11px", fontWeight: 600, fontFamily: dt.font, color: dt.decommissioned.badge.text, backgroundColor: dt.decommissioned.badge.bg, border: `1px solid ${dt.decommissioned.badge.border}`, borderRadius: "10px", padding: "2px 8px", lineHeight: "16px" }}>
              {translate("decommissioned")}
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
          {showActions && (
            <Box onClick={stopProp}>
              <TableActionButton stopPropagation>
                <MenuItem onClick={(e) => { e.stopPropagation(); navigate(`/machinery/${machine.id}`); }}><TuneOutlined /> {translate("profile")}</MenuItem>
                {isDecommissioned ? (
                  <MenuItem onClick={(e) => { e.stopPropagation(); onRestore?.(machine); }} sx={{ color: "#22C55E" }}>
                    <RestoreOutlined sx={{ color: "#22C55E" }} /> {translate("restore")}
                  </MenuItem>
                ) : (
                  <>
                    {/* QR -- Write+ since D7-FE1 (SEC-REQ-B7-34 option (i)): the dialog now encodes the nameplate id-link from a Machinery:Write GET. Do not ungate. */}
                    <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                      <MenuItem onClick={(e) => { e.stopPropagation(); onQRCode?.(machine); }}><QrCodeIcon /> {translate("qrCode")}</MenuItem>
                    </PermissionGuard>
                    <MenuItem onClick={(e) => { e.stopPropagation(); onCreateServiceReport?.(machine); }}><BuildOutlined /> {translate("createServiceReport")}</MenuItem>
                    <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                      <MenuItem onClick={(e) => { e.stopPropagation(); onCreateServiceRequest?.(machine); }}><BuildOutlined /> {translate("createServiceRequest")}</MenuItem>
                    </PermissionGuard>
                    <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                      <MenuItem onClick={(e) => { e.stopPropagation(); (onEditDetails || onEdit)?.(machine); }}><EditOutlinedIcon /> {translate("editDetails")}</MenuItem>
                    </PermissionGuard>
                    <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                      <MenuItem onClick={(e) => { e.stopPropagation(); onDuplicate?.(machine); }}><ContentCopy /> {translate("duplicate")}</MenuItem>
                    </PermissionGuard>
                    {onAssignGroup && (
                      <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                        <MenuItem onClick={(e) => { e.stopPropagation(); onAssignGroup(machine); }}><GroupWorkIcon /> {translate("assignToGroup")}</MenuItem>
                      </PermissionGuard>
                    )}
                    <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                      <MenuItem onClick={(e) => { e.stopPropagation(); onDecommission?.(machine); }} sx={{ color: dt.decommissioned.amber }}>
                        <ArchiveOutlined sx={{ color: dt.decommissioned.amber }} /> {translate("decommission")}
                      </MenuItem>
                    </PermissionGuard>
                  </>
                )}
              </TableActionButton>
            </Box>
          )}
        </Box>

        <Box sx={{
          display: "flex", justifyContent: "center", mb: "12px",
          filter: isDecommissioned ? dt.decommissioned.card.imageFilter : "none",
          transition: "filter 0.2s ease",
        }}>
          <ImageGallery images={galleryImages} variant="card" />
        </Box>

        <Box sx={{ textAlign: "center", mb: "10px" }}>
          <Box sx={{ fontSize: "15px", fontWeight: 600, fontFamily: dt.font, color: dt.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayName}</Box>
          {subtitle && <Box sx={{ fontSize: "12px", fontFamily: dt.font, color: dt.text.secondary, mt: "2px" }}>{subtitle}</Box>}
        </Box>

        {/* Location line */}
        {!isDecommissioned && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", mb: "8px", minWidth: 0 }}>
            <PlaceOutlined sx={{ fontSize: "14px", color: locationDisplay ? dt.blue : dt.text.tertiary, flexShrink: 0 }} />
            <Box sx={{
              fontSize: "12px", fontFamily: dt.font, fontWeight: locationDisplay ? 500 : 400,
              color: locationDisplay ? dt.text.secondary : dt.text.tertiary,
              fontStyle: locationDisplay ? "normal" : "italic",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
            }}>
              {locationDisplay || translate("locationNotSet")}
            </Box>
          </Box>
        )}

        {isDecommissioned && machine.decommissionedDate && (
          <Box sx={{ textAlign: "center", fontSize: "11px", fontFamily: dt.fontMono, color: dt.decommissioned.badge.text, mb: "4px" }}>
            {translate("decommissionedOn", { date: new Date(machine.decommissionedDate).toLocaleDateString() })}
          </Box>
        )}
        {isDecommissioned && machine.decommissionReason && (
          <Box sx={{
            textAlign: "center", fontSize: "11px", fontFamily: dt.font, fontStyle: "italic",
            color: dt.text.tertiary, mb: "8px", overflow: "hidden", textOverflow: "ellipsis",
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          }}>
            &ldquo;{machine.decommissionReason}&rdquo;
          </Box>
        )}

        {!isDecommissioned && (
          <>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px", mb: "10px" }}>
              <SpecRow label={translate("sn")} value={machine?.serialNumber} />
              <SpecRow label={translate("productionYear")} value={yearDisplay} />
              <SpecRow label={translate("machine")} value={machine?.internalNumber ? `#${machine.internalNumber}` : undefined} />
              <SpecRow label={translate("system")} value={machine?.controlSystemBrand?.name} />
            </Box>

            {/* DR-4: MIDAS readiness row — full-width, under the spec grid (opt-in, item 8). */}
            {showMidasRow && (
              <MidasCardRow machine={machine} connected={connected} variant="horizontal" />
            )}

            {effectiveGroupNames.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", mt: "6px" }}>
                {effectiveGroupNames.map((name: string) => {
                  const color = getGroupColor(name);
                  return (
                    <Box key={name} sx={{ fontSize: dt.machinery.groupPill.fontSize, fontWeight: 500, fontFamily: dt.font, color: color, backgroundColor: `${color}14`, borderRadius: dt.machinery.groupPill.radius, padding: `${dt.machinery.groupPill.py} ${dt.machinery.groupPill.px}`, whiteSpace: "nowrap", lineHeight: "16px" }}>
                      {name}
                    </Box>
                  );
                })}
              </Box>
            )}

          </>
        )}
      </Box>
    );
  }

  // =====================================================================
  //  DESKTOP / TABLET LAYOUT
  // =====================================================================

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        background: dt.machinery.card.bg,
        border: isDecommissioned ? dt.decommissioned.card.borderStyle : dt.machinery.card.border,
        borderRadius: dt.machinery.card.radius, padding: "20px",
        boxShadow: dt.machinery.card.shadow, position: "relative", fontFamily: dt.font,
        display: "flex", flexDirection: "column",
        opacity: isDecommissioned ? dt.decommissioned.card.opacity : 1,
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        ...(isClickable ? { cursor: "pointer", "&:hover": { transform: "translateY(-2px)", boxShadow: dt.machinery.card.shadowHover, opacity: isDecommissioned ? dt.decommissioned.card.opacityHover : 1 } } : {}),
        ...(sx as any),
      }}
    >
      {/* NEW badge - desktop/tablet layout */}
      {isNew && (
        <Box
          sx={{
            position: "absolute",
            top: "12px",
            right: "12px",
            zIndex: 2,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: dt.machinery.newBadge.bg,
            color: dt.machinery.newBadge.text,
            border: `1px solid ${dt.machinery.newBadge.border}`,
            borderRadius: "10px",
            padding: "2px 10px",
            fontSize: "10px",
            fontWeight: 700,
            fontFamily: dt.font,
            textTransform: "uppercase",
            letterSpacing: "1.2px",
            lineHeight: "16px",
            whiteSpace: "nowrap",
            pointerEvents: "none",
            animation: "newMachinePulse 2s ease-in-out 3",
          }}
        >
          {translate("new")}
        </Box>
      )}
      {/* Health indicator dot -- absolute top-right, matching Card view */}
      {showActions && !isDecommissioned && (
        <Tooltip title={healthTooltip} arrow placement="top">
          <Box
            sx={{
              position: "absolute",
              top: "12px",
              right: "12px",
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: healthColor,
              boxShadow: `0 0 6px ${healthColor}80`,
              zIndex: 1,
              transition: "transform 0.2s ease",
              "&:hover": { transform: "scale(1.3)" },
            }}
          />
        </Tooltip>
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "12px" }}>
        {isDecommissioned ? (
          <Box sx={{ fontSize: "11px", fontWeight: 600, fontFamily: dt.font, color: dt.decommissioned.badge.text, backgroundColor: dt.decommissioned.badge.bg, border: `1px solid ${dt.decommissioned.badge.border}`, borderRadius: "10px", padding: "2px 8px", lineHeight: "16px" }}>
            {translate("decommissioned")}
          </Box>
        ) : (
          <Box />
        )}

        {showActions && (
          <Box onClick={stopProp} sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
            {/* WhatsApp sunset 2026-03-14 — channel deactivated, web chat is sole channel */}
            {/* {!isDecommissioned && machine.botActivated && (
              <IconButton onClick={(e) => { e.stopPropagation(); onWhatsAppBot?.(machine); }} sx={{ padding: "4px", mr: "4px", "&:hover": { backgroundColor: "transparent" } }}>
                <img src={whatsappLogo} alt="WhatsApp" style={{ width: 28, height: 28 }} />
              </IconButton>
            )} */}
            <TableActionButton stopPropagation>
              <MenuItem onClick={(e) => { e.stopPropagation(); navigate(`/machinery/${machine.id}`); }}><TuneOutlined /> {translate("profile")}</MenuItem>
              {isDecommissioned ? (
                <MenuItem onClick={(e) => { e.stopPropagation(); onRestore?.(machine); }} sx={{ color: "#22C55E" }}>
                  <RestoreOutlined sx={{ color: "#22C55E" }} /> {translate("restore")}
                </MenuItem>
              ) : (
                <>
                  {/* QR -- Write+ since D7-FE1 (SEC-REQ-B7-34 option (i)): the dialog now encodes the nameplate id-link from a Machinery:Write GET. Do not ungate. */}
                  <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                    <MenuItem onClick={(e) => { e.stopPropagation(); onQRCode?.(machine); }}><QrCodeIcon /> {translate("qrCode")}</MenuItem>
                  </PermissionGuard>
                  <MenuItem onClick={(e) => { e.stopPropagation(); onCreateServiceReport?.(machine); }}><BuildOutlined /> {translate("createServiceReport")}</MenuItem>
                  <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                    <MenuItem onClick={(e) => { e.stopPropagation(); onCreateServiceRequest?.(machine); }}><BuildOutlined /> {translate("createServiceRequest")}</MenuItem>
                  </PermissionGuard>
                  <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                    <MenuItem onClick={(e) => { e.stopPropagation(); (onEditDetails || onEdit)?.(machine); }}><EditOutlinedIcon /> {translate("editDetails")}</MenuItem>
                  </PermissionGuard>
                  <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                    <MenuItem onClick={(e) => { e.stopPropagation(); onDuplicate?.(machine); }}><ContentCopy /> {translate("duplicate")}</MenuItem>
                  </PermissionGuard>
                  {onAssignGroup && (
                    <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                      <MenuItem onClick={(e) => { e.stopPropagation(); onAssignGroup(machine); }}><GroupWorkIcon /> {translate("assignToGroup")}</MenuItem>
                    </PermissionGuard>
                  )}
                  <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                    <MenuItem onClick={(e) => { e.stopPropagation(); onDecommission?.(machine); }} sx={{ color: dt.decommissioned.amber }}>
                      <ArchiveOutlined sx={{ color: dt.decommissioned.amber }} /> {translate("decommission")}
                    </MenuItem>
                  </PermissionGuard>
                </>
              )}
            </TableActionButton>
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", gap: "20px", flex: 1 }}>
        <Box sx={{
          flexShrink: 0,
          filter: isDecommissioned ? dt.decommissioned.card.imageFilter : "none",
          transition: "filter 0.2s ease",
        }}>
          <ImageGallery images={galleryImages} variant="card" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Box sx={{ fontSize: "16px", fontWeight: 600, fontFamily: dt.font, color: dt.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: 1.3 }}>{displayName}</Box>
          {subtitle && <Box sx={{ fontSize: "13px", fontFamily: dt.font, fontWeight: 400, color: dt.text.secondary, mt: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</Box>}
          {brandName && (
            <Box sx={{ mt: "2px", display: "flex", alignItems: "baseline", gap: "4px", flexWrap: "wrap" }}>
              {machine.catalogModel?.manufacturer?.parentManufacturerName ? (
                <>
                  <Box component="span" sx={{ fontSize: "12px", fontFamily: dt.font, fontWeight: 400, color: "#9CA3AF" }}>
                    {machine.catalogModel.manufacturer.parentManufacturerName}
                  </Box>
                  <Box component="span" sx={{ fontSize: "11px", color: "#9CA3AF" }}>&gt;</Box>
                  <Box component="span" sx={{ fontSize: "13px", fontFamily: dt.font, fontWeight: 500, color: dt.text.secondary }}>
                    {machine.catalogModel.manufacturer.name}
                  </Box>
                </>
              ) : (
                <Box component="span" sx={{ fontSize: "13px", fontFamily: dt.font, fontWeight: 500, color: dt.text.secondary }}>
                  {brandName}
                </Box>
              )}
            </Box>
          )}

          {/* Location line -- desktop/tablet */}
          {!isDecommissioned && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mt: "6px", minWidth: 0 }}>
              <PlaceOutlined sx={{ fontSize: "14px", color: locationDisplay ? dt.blue : dt.text.tertiary, flexShrink: 0 }} />
              <Box sx={{
                fontSize: "12px", fontFamily: dt.font, fontWeight: locationDisplay ? 500 : 400,
                color: locationDisplay ? dt.text.secondary : dt.text.tertiary,
                fontStyle: locationDisplay ? "normal" : "italic",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
              }}>
                {locationDisplay || translate("locationNotSet")}
              </Box>
            </Box>
          )}

          {isDecommissioned && machine.decommissionedDate && (
            <Box sx={{ fontSize: "11px", fontFamily: dt.fontMono, color: dt.decommissioned.badge.text, mt: "6px" }}>
              {translate("decommissionedOn", { date: new Date(machine.decommissionedDate).toLocaleDateString() })}
            </Box>
          )}
          {isDecommissioned && machine.decommissionReason && (
            <Box sx={{
              fontSize: "11px", fontFamily: dt.font, fontStyle: "italic", color: dt.text.tertiary,
              mt: "4px", overflow: "hidden", textOverflow: "ellipsis",
              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
            }}>
              &ldquo;{machine.decommissionReason}&rdquo;
            </Box>
          )}

          {!isDecommissioned && (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px", mt: "10px" }}>
              <SpecRow label={translate("sn")} value={machine?.serialNumber} />
              <SpecRow label={translate("productionYear")} value={yearDisplay} />
              <SpecRow
                label={translate("system")}
                value={machine?.controlSystemBrand?.name ? (machine.system ? `${machine.controlSystemBrand.name} / ${machine.system}` : machine.controlSystemBrand.name) : machine?.system || undefined}
              />
              {machine?.maxSpindleRpm ? (
                <SpecRow label={translate("rpm")} value={machine.maxSpindleRpm.toLocaleString() + " RPM"} />
              ) : (
                <SpecRow label={translate("machine")} value={machine?.internalNumber ? `#${machine.internalNumber}` : undefined} />
              )}
            </Box>
          )}

          {/* DR-4: MIDAS readiness row — full-width, under the spec grid (opt-in, item 8). */}
          {showMidasRow && !isDecommissioned && (
            <MidasCardRow machine={machine} connected={connected} variant="horizontal" />
          )}
        </Box>
      </Box>

      {!isDecommissioned && (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: "12px", minHeight: "24px" }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px", alignItems: "center", minWidth: 0, flex: 1 }}>
            {effectiveGroupNames.map((name: string) => {
              const color = getGroupColor(name);
              return (
                <Box key={name} sx={{ fontSize: dt.machinery.groupPill.fontSize, fontWeight: 500, fontFamily: dt.font, color: color, backgroundColor: `${color}14`, borderRadius: dt.machinery.groupPill.radius, padding: `${dt.machinery.groupPill.py} ${dt.machinery.groupPill.px}`, whiteSpace: "nowrap", lineHeight: "16px" }}>
                  {name}
                </Box>
              );
            })}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            {!machine.serialNumber && showActions && (
              <Box sx={{ fontSize: "11px", fontWeight: 600, fontFamily: dt.font, color: "#F23E3E", whiteSpace: "nowrap" }}>
                {translate("machineSnMissing")}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default RedesignedMachineCard;
